// Authentication API Service
import apiClient from './index';
import { setToken, removeToken, setUserId, removeUserId } from './config';
import { getLoginErrorMessage, getSignupErrorMessage, isNetworkError } from '../../utils/errorMessages';

/**
 * Login user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} API response with user data and token
 */
export const login = async (email, password) => {
  try {
    const response = await apiClient.post('/login', {
      email,
      password,
    });

    // Handle successful login
    if (response.success && response.data) {
      const { user, token } = response.data;

      // Store token
      if (token) {
        setToken(token);
      }

      // Store user ID
      if (user?.user_id) {
        setUserId(user.user_id);
      }

      return {
        success: true,
        data: response.data,
        message: response.message || 'Login successful',
      };
    }

    // Handle API returned success: false
    return {
      success: false,
      message: getLoginErrorMessage(response.message || response.error),
      code: response.code || response.error_code,
    };
  } catch (error) {
    // Handle network or server errors
    const isNetwork = isNetworkError(error);
    
    return {
      success: false,
      message: getLoginErrorMessage(error.message || error.error || error),
      isNetworkError: isNetwork,
      code: error.code || error.error_code,
    };
  }
};

/**
 * Register/Signup new user
 * @param {object} userData - User registration data
 * @returns {Promise} API response
 */
export const signup = async (userData) => {
  try {
    const response = await apiClient.post('/users', userData);

    if (response.success) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Registration successful',
      };
    }

    // Handle API returned success: false
    return {
      success: false,
      message: getSignupErrorMessage(response.message || response.error),
      code: response.code || response.error_code,
    };
  } catch (error) {
    // Handle network or server errors
    const isNetwork = isNetworkError(error);
    
    return {
      success: false,
      message: getSignupErrorMessage(error.message || error.error || error),
      isNetworkError: isNetwork,
      code: error.code || error.error_code,
    };
  }
};

/**
 * Logout user
 * @returns {Promise} API response
 */
export const logout = async () => {
  try {
    await apiClient.post('/logout');
    
    // Clear local storage
    removeToken();
    removeUserId();
    
    return {
      success: true,
      message: 'Logout successful',
    };
  } catch (error) {
    // Even if API call fails, clear local storage
    removeToken();
    removeUserId();
    
    return {
      success: false,
      message: error.message || 'Logout failed',
      error: error.error || error.message,
    };
  }
};

/**
 * Request password reset OTP
 * @param {string} email - User email
 * @returns {Promise} API response
 */
export const forgotPassword = async (email) => {
  try {
    const response = await apiClient.post('/forgot-password', { email });
    return {
      success: !!response.success,
      data: response.data,
      message: response.message || 'OTP has been sent to your email address.',
      code: response.code || response.error_code,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Failed to send OTP. Please try again.',
      code: error.code || error.error_code,
      statusCode: error.statusCode,
    };
  }
};

/**
 * Verify password reset OTP
 * @param {string} email - User email
 * @param {string} otp - 6-digit OTP
 * @returns {Promise} API response
 */
export const verifyResetOtp = async (email, otp) => {
  try {
    const response = await apiClient.post('/verify-otp', { email, otp });
    return {
      success: !!response.success,
      data: response.data,
      message: response.message || 'OTP verified successfully.',
      code: response.code || response.error_code,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Invalid OTP. Please try again.',
      code: error.code || error.error_code,
      statusCode: error.statusCode,
    };
  }
};

/**
 * Reset password using verified OTP
 * @param {object} payload - reset password payload
 * @returns {Promise} API response
 */
export const resetPassword = async (payload) => {
  try {
    const response = await apiClient.post('/reset-password', payload);
    return {
      success: !!response.success,
      data: response.data,
      message: response.message || 'Password has been reset successfully.',
      code: response.code || response.error_code,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Failed to reset password. Please try again.',
      code: error.code || error.error_code,
      statusCode: error.statusCode,
    };
  }
};

